import { Button, Input, Select, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ClassPill, PersonAvatar, Pill, PriorityPill } from 'pages/support-shared/components';
import { SupportBox, SupportMeta, SupportSummary, ThreadRow } from 'pages/support-shared/types';
import { chatTime, durationText, STATUS_STYLE, waitingTone } from 'pages/support-shared/utils';
import styles from './styles.module.scss';

export interface ListFilter {
  box: SupportBox;
  q: string;
  userClass?: string;
  category?: string;
}

const BOXES: { key: SupportBox; label: string; count?: (s: SupportSummary) => number; strong?: boolean }[] = [
  { key: 'waiting', label: 'Chờ trả lời', count: (s) => s.waiting, strong: true },
  { key: 'mine', label: 'Của tôi', count: (s) => s.mine },
  { key: 'pending', label: 'Chờ người dùng', count: (s) => s.pending },
  { key: 'resolved', label: 'Đã xong' },
  { key: 'all', label: 'Tất cả' },
];

interface Props {
  filter: ListFilter;
  onFilter: (next: ListFilter) => void;
  summary: SupportSummary | null;
  meta: SupportMeta | null;
  items: ThreadRow[];
  total: number;
  loading: boolean;
  selectedId?: number;
  onSelect: (id: number) => void;
  onLoadMore?: () => void;
  now: number;
}

const ThreadList = ({
  filter,
  onFilter,
  summary,
  meta,
  items,
  total,
  loading,
  selectedId,
  onSelect,
  onLoadMore,
  now,
}: Props) => (
  <div className={`${styles.column} ${styles.list}`}>
    <div className={styles.listHead}>
      <div className={styles.boxTabs} role="tablist" aria-label="Hộp thư">
        {BOXES.map((box) => {
          const active = filter.box === box.key;
          const count = summary && box.count ? box.count(summary) : undefined;
          return (
            <button
              key={box.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`${styles.boxTab} ${active ? styles.boxTabActive : ''}`}
              onClick={() => onFilter({ ...filter, box: box.key })}
            >
              {box.label}
              {!!count && (
                <span className={`${styles.boxCount} ${box.strong ? '' : styles.boxCountMuted}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Tìm tên, username, UID TikTok, id"
        defaultValue={filter.q}
        onPressEnter={(e) => onFilter({ ...filter, q: (e.target as HTMLInputElement).value.trim() })}
        onChange={(e) => {
          if (!e.target.value) onFilter({ ...filter, q: '' });
        }}
      />
      <div className={styles.filters}>
        <Select
          allowClear
          size="small"
          placeholder="Nhóm người dùng"
          value={filter.userClass}
          onChange={(value) => onFilter({ ...filter, userClass: value })}
          options={(meta?.classes || []).filter((c) => c.value !== 'MANAGEMENT')}
        />
        <Select
          allowClear
          size="small"
          placeholder="Loại vấn đề"
          value={filter.category}
          onChange={(value) => onFilter({ ...filter, category: value })}
          options={meta?.categories || []}
          popupMatchSelectWidth={false}
        />
      </div>
    </div>

    <div className={styles.listBody}>
      {loading && !items.length ? (
        <div className={styles.emptyBox}>
          <Spin />
        </div>
      ) : !items.length ? (
        <div className={styles.emptyBox}>
          {filter.box === 'waiting' ? 'Không còn ai đang chờ trả lời. 🎉' : 'Không có cuộc hỗ trợ nào.'}
        </div>
      ) : (
        items.map((row) => {
          const waiting = row.status === 'OPEN' && row.awaitingSince;
          const tone = waitingTone(row.awaitingSince, now);
          const noRequest = row.status === 'RESOLVED' && !row.lastUserMessageAt;
          return (
            <button
              key={row.id}
              type="button"
              className={`${styles.threadItem} ${row.id === selectedId ? styles.threadItemActive : ''}`}
              onClick={() => onSelect(row.id)}
            >
              <PersonAvatar name={row.user.name} avatar={row.user.avatar} size={40} />
              <div className={styles.threadMain}>
                <div className={styles.threadTop}>
                  <span className={styles.threadName}>{row.user.name}</span>
                  <span className={styles.threadTime}>{chatTime(row.lastMessageAt)}</span>
                </div>
                <div className={`${styles.threadPreview} ${row.unreadCount ? styles.threadPreviewUnread : ''}`}>
                  <span>
                    {row.lastMessageDirection === 'OUT' ? 'Bạn: ' : ''}
                    {row.lastMessagePreview || '—'}
                  </span>
                  {!!row.unreadCount && <span className={styles.unreadDot}>{row.unreadCount}</span>}
                </div>
                <div className={styles.threadMeta}>
                  <ClassPill value={row.userClass} label={row.userClassLabel} />
                  {filter.box !== 'waiting' && !noRequest && (
                    <Pill color={STATUS_STYLE[row.status].color} bg={STATUS_STYLE[row.status].bg}>
                      {STATUS_STYLE[row.status].label}
                    </Pill>
                  )}
                  {noRequest && <span>Chưa có yêu cầu</span>}
                  <PriorityPill priority={row.priority} />
                  {waiting && (
                    <Pill color={tone.color} bg={tone.bg} title="Thời gian người dùng đã chờ">
                      chờ {durationText(row.awaitingSince, now)}
                    </Pill>
                  )}
                  {row.assignee ? <span>· {row.assignee.name}</span> : row.status === 'OPEN' ? <span>· chưa ai nhận</span> : null}
                </div>
              </div>
            </button>
          );
        })
      )}
    </div>
    {items.length < total && onLoadMore && (
      <div className={styles.listFooter}>
        <Button size="small" onClick={onLoadMore} loading={loading}>
          Xem thêm ({total - items.length})
        </Button>
      </div>
    )}
  </div>
);

export default ThreadList;
